import { Box } from '@mantine/core';
import { CSSProperties, ReactElement, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useMarketOptions } from '@/hooks/useMarketOptions';
import { useCalendarDay, useNow } from '@/hooks/useNow';
import { alert } from '@/services/alert';
import { getRateLimitAlert, isMedplumRateLimitError } from '@/utils/medplumRateLimit';
import { ALL_POST_CONSULTATION_CONSULTS_QUERY } from '../../graphql';
import { useHubFilterValues } from '../../hooks/useHubFilterValues';
import { usePacQueueHydration } from '../../hooks/usePacQueueHydration';
import { usePatientListFilters } from '../../hooks/usePatientListFilters';
import { QUEUE_PAGE_ROWS, useQueuePage } from '../../hooks/useQueuePage';
import { ConsultAppointmentResult } from '../../services/listRowShared';
import { buildNoShowFilter, isInNoShowScope, noShowSubscriptionParams } from '../../services/noShowFilters';
import { buildNoShowRows, NoShowLeadRow } from '../../services/noShowRows';
import { lastCommunicationFloor } from '../../services/pacQueueHydration';
import {
  isQueueFiltered,
  LEAD_STATUS_FILTER_OPTIONS,
  matchesPatientFilterTags,
  optionsFromRows,
  QUEUE_FILTER_ANY,
  QUEUE_FILTERS_OFF,
  QueueFilterValues,
  TRACK_FILTER_OPTIONS,
} from '../../services/queueFilters';
import { ListRangeControl } from '@/components/Hub/ListRangeControl';
import { QueueFilterBar } from '@/components/Hub/QueueFilterBar';
import { QueueFooter } from '@/components/Hub/QueueFooter';
import { QueueTable } from '../../components/QueueTable';
import { lastContactColumn, patientColumn, paymentTrackColumn, pnColumn } from './hubColumns';
import { notesColumn } from './notesColumn';
import { missedConsultColumn } from './postConsultColumns';
import { statusColumn } from './statusColumn';
import { EditedRow, useHubRowEdits } from './useHubRowEdits';

/**
 * The Intake hub's Consultation no-show tab (BAR-2809): patients whose latest
 * consult was missed or cancelled, with nothing upcoming booked. One row per
 * patient, on their `no-show-consult` (BAR-2888), so it pages 100 at a time with
 * a true total; the filters run on the server (BAR-2885).
 */

const DEFAULT_PRESET = 'all';

const NO_SHOW_PRESETS = [
  { id: 'all' as const, label: 'All time' },
  { id: 'last-30' as const, label: 'Last 30 days' },
  { id: 'last-60' as const, label: 'Last 60 days' },
];

const DEFAULTS: QueueFilterValues = { ...QUEUE_FILTERS_OFF, status: 'active' };

type HubNoShowRow = EditedRow<NoShowLeadRow>;

const HYDRATION_NEEDS = {
  consult: true,
  procedure: true,
  callNotes: true,
  careTeam: true,
  eligibility: true,
  stickyNotes: true,
} as const;

const TAB_STYLE: CSSProperties = { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' };

export interface NoShowTabProps {
  /** Injectable for tests; defaults to a clock that keeps ticking. */
  now?: Date;
}

export function NoShowTab({ now }: NoShowTabProps): ReactElement {
  const navigate = useNavigate();
  const resolvedNow = useNow(now);
  const today = useCalendarDay(resolvedNow);

  const filters = usePatientListFilters<NoShowLeadRow>({
    resetKey: 'intake-no-show',
    chipDefinitions: [],
    defaultPreset: DEFAULT_PRESET,
    now: resolvedNow,
  });

  const { values, setValues, setFilter, defaults, pnOptions } = useHubFilterValues(DEFAULTS);
  const filter = useMemo(() => buildNoShowFilter(filters.range, values), [filters.range, values]);
  const subscriptionParams = useMemo(() => noShowSubscriptionParams(), []);

  const {
    rows: data,
    total,
    loading,
    error,
    page,
    setPage,
  } = useQueuePage(ALL_POST_CONSULTATION_CONSULTS_QUERY, 'Appointment', filter, subscriptionParams);

  useEffect(() => {
    if (error && isMedplumRateLimitError(error)) {
      alert.warning({
        ...getRateLimitAlert(
          `The system temporarily limited how fast we can load the consultation no-show queue.
          Wait a few seconds before trying again.`
        ),
      });
    }
  }, [error]);

  const missedConsults = useMemo(
    () =>
      error ? [] : (data as ConsultAppointmentResult[]).filter((consult) => isInNoShowScope(consult, filters.range)),
    [data, error, filters.range]
  );

  const patientRefs = useMemo(
    () => [
      ...new Set(
        missedConsults
          .flatMap((consult) => consult.participant ?? [])
          .map((participant) => participant.actor?.resource)
          .filter((resource) => resource?.resourceType === 'Patient')
          .map((resource) => `Patient/${(resource as { id?: string }).id}`)
      ),
    ],
    [missedConsults]
  );

  const hydration = usePacQueueHydration(
    patientRefs,
    { ...HYDRATION_NEEDS, lastCommunicationSince: lastCommunicationFloor(today) },
    `intake-no-show:${filter}`
  );

  const builtRows = useMemo(
    () => buildNoShowRows(missedConsults, hydration, resolvedNow),
    [missedConsults, hydration, resolvedNow]
  );
  const edits = useHubRowEdits(builtRows, values.status);
  const { rows } = edits;

  // Rows that arrive by subscription skip the query's filter, so PN, track and location
  // are checked on their tags too. Status reads the row, so a change made here applies at once.
  const visibleRows = useMemo(
    () =>
      rows.filter(
        (row) =>
          matchesPatientFilterTags(row.patient, { ...values, status: QUEUE_FILTER_ANY }) &&
          (values.status === QUEUE_FILTER_ANY || row.activeLead.status === values.status)
      ),
    [rows, values]
  );
  const { markets } = useMarketOptions();
  const locationOptions = useMemo(() => optionsFromRows(markets, 'All'), [markets]);

  const { onNoteSaved, changeStatus, undoStatus } = edits;
  const columns = useMemo(
    () => [
      patientColumn<HubNoShowRow>(),
      paymentTrackColumn<HubNoShowRow>(),
      pnColumn<HubNoShowRow>(),
      missedConsultColumn<HubNoShowRow>(),
      lastContactColumn<HubNoShowRow>(),
      notesColumn<HubNoShowRow>({ onSaved: onNoteSaved }),
      statusColumn<HubNoShowRow>({ onChange: changeStatus, onUndo: undoStatus }),
    ],
    [onNoteSaved, changeStatus, undoStatus]
  );

  return (
    <Box style={TAB_STYLE}>
      <QueueFilterBar
        date={
          <ListRangeControl
            label="No-show"
            presets={NO_SHOW_PRESETS}
            presetId={filters.presetId}
            value={filters.range}
            onChange={filters.setRange}
          />
        }
        filters={[
          { label: 'PN', value: values.pn, options: pnOptions, onChange: setFilter('pn') },
          { label: 'Payment track', value: values.track, options: TRACK_FILTER_OPTIONS, onChange: setFilter('track') },
          { label: 'Location', value: values.location, options: locationOptions, onChange: setFilter('location') },
          { label: 'Status', value: values.status, options: LEAD_STATUS_FILTER_OPTIONS, onChange: setFilter('status') },
        ]}
        isFiltered={isQueueFiltered(values, defaults) || filters.presetId !== DEFAULT_PRESET}
        onReset={() => {
          setValues(defaults);
          filters.setRange(DEFAULT_PRESET);
        }}
      />
      <QueueTable
        columns={columns}
        data={visibleRows}
        loading={loading || hydration.loading}
        initialSorting={[{ id: 'consult', desc: true }]}
        onOpenPatient={(patientId) => navigate(`/Patient/${patientId}/timeline`)}
        getRowClassName={edits.rowClassName}
      />
      <QueueFooter
        shown={visibleRows.length}
        total={total}
        page={page}
        pageCount={Math.ceil((total ?? 0) / QUEUE_PAGE_ROWS)}
        onPageChange={setPage}
      />
    </Box>
  );
}
